import { memo, type ReactNode, useEffect, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { cn } from '@air/utils-tailwind';

import {
  WORKSPACE_AGENT_CONSENT_DOCK_DEFAULT_APPROVE_LABEL,
  WORKSPACE_AGENT_DOCK_KEY_ARM_DELAY_MS,
  workspaceAgentConsentDockShell,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import { WorkspaceAgentDockActions } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/consent/WorkspaceAgentDockActions';
import { WorkspaceAgentDockFrame } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/consent/WorkspaceAgentDockFrame';
import { applyWorkspaceAgentDockKey } from '~/components/v2/components/workspace/workspace-agent-panel/utils/applyWorkspaceAgentDockKey';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

export type WorkspaceAgentPanelConsentDockProps = {
  children?: ReactNode;
  approveLabel?: string;
  cancelLabel?: string;
  className?: string;
  'data-testid'?: string;
  /** A gray bar under the buttons (e.g. the remember-for-session box). */
  footerLeading?: ReactNode;
  /**
   * For docks whose body is a form: approving before it says anything would submit a no-op. Decided by
   * the caller rather than the body, because the body does not own this button.
   */
  isApproveDisabled?: boolean;
  onApprove: () => void;
  onCancel: () => void;
  /** Number keys 1 to 9 pick these options; the chips show the same numbers. */
  onPickOption?: (index: number) => void;
  optionCount?: number;
  /** The agent's question. Private ask docks pass chips here; stories and tool-consent pass a string. */
  title?: ReactNode;
};

/** Covers the composer in-flow while an elicitation is pending; the composer stays mounted underneath as the sizer. */
export const WorkspaceAgentPanelConsentDock = memo(
  ({
    approveLabel = WORKSPACE_AGENT_CONSENT_DOCK_DEFAULT_APPROVE_LABEL,
    cancelLabel = 'Cancel',
    children,
    className,
    'data-testid': testId,
    footerLeading,
    isApproveDisabled = false,
    onApprove,
    onCancel,
    onPickOption,
    optionCount = 0,
    title,
  }: WorkspaceAgentPanelConsentDockProps) => {
    const dialogRef = useRef<HTMLElement>(null);
    const focusComposer = useWorkspaceAgentPanelStore((store) => store.focusComposer);
    const headingId = `consent-dock-title-${testId ?? 'default'}`;
    const bodyId = `consent-dock-body-${testId ?? 'default'}`;
    const hasTitle = title != null && title !== '';
    const hasBody = hasTitle || children != null;

    /*
     * Keyboard users should land on the dock when it opens, not on the composer it replaced.
     *
     * The region itself takes focus rather than the primary button, because a dock whose body is a form
     * opens with that button disabled — and a disabled button cannot be focused, so aiming at it would
     * silently do nothing in exactly the case that needs it most.
     *
     * `preventScroll: true` keeps the focus handoff from dragging the page: the dock sits at the
     * bottom of a panel that can extend below the viewport, and the browser's default focus-scroll
     * would otherwise move the body up to bring the dock into view, dragging the fixed panel
     * under the workspace top bar and partially obscuring its own header. The user is already
     * looking at the panel — the keyboard landing point matters, the screen scroll does not.
     */
    useEffect(() => {
      dialogRef.current?.focus({ preventScroll: true });
    }, []);

    /*
     * Layout cleanup, not passive: by the time a passive cleanup runs React has already removed this
     * subtree and focus has fallen to `<body>`, so a containment check there can never be true. The
     * node is captured because the ref is detached before the cleanup too.
     */
    useIsomorphicLayoutEffect(() => {
      const dock = dialogRef.current;

      return () => {
        // Only hand focus back when the dock still owns it. Minimize/tasks-switch set their own
        // `pendingFocus`, and clobbering that would strand the caret.
        if (dock?.contains(document.activeElement)) {
          focusComposer();
        }
      };
    }, [focusComposer]);

    const latest = useRef({ isApproveDisabled, onApprove, onCancel, onPickOption, optionCount });
    latest.current = { isApproveDisabled, onApprove, onCancel, onPickOption, optionCount };

    useEffect(() => {
      const dock = dialogRef.current;

      // biome-ignore lint/suspicious/noUnnecessaryConditions: the ref is populated by React on mount; Biome only sees the initial `null`.
      if (!dock) {
        return;
      }

      let armAt = performance.now() + WORKSPACE_AGENT_DOCK_KEY_ARM_DELAY_MS;

      const handleKeyDown = (event: KeyboardEvent) => {
        const { isApproveDisabled, onApprove, onCancel, onPickOption, optionCount } = latest.current;

        armAt = applyWorkspaceAgentDockKey(event, dock, {
          armAt,
          isApproveDisabled,
          now: performance.now(),
          onApprove,
          onCancel,
          onPickOption,
          optionCount,
        });
      };

      document.addEventListener('keydown', handleKeyDown, true);

      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }, []);

    return (
      <section
        aria-describedby={children != null ? bodyId : undefined}
        aria-label={hasTitle ? undefined : 'Choose an option'}
        aria-labelledby={hasTitle ? headingId : undefined}
        className={cn(workspaceAgentConsentDockShell(), !hasTitle && 'flex-none', className)}
        data-testid={testId}
        ref={dialogRef}
        tabIndex={-1}
      >
        <WorkspaceAgentDockFrame bodyId={bodyId} tabIndex={0} title={title} titleId={headingId}>
          {children}
        </WorkspaceAgentDockFrame>
        <WorkspaceAgentDockActions
          approveLabel={approveLabel}
          cancelLabel={cancelLabel}
          hasDivider={hasBody && footerLeading == null}
          isApproveDisabled={isApproveDisabled}
          onApprove={onApprove}
          onCancel={onCancel}
        />
        {footerLeading != null ? <div className="shrink-0 ads:bg-raised bg-gray-2 p-4">{footerLeading}</div> : null}
      </section>
    );
  },
);

WorkspaceAgentPanelConsentDock.displayName = 'WorkspaceAgentPanelConsentDock';
